/**
 * Normalizes data for the SpotlightContainer from different sources into a consistent structure.
 * Priority: direct slotResp (from useDataModel('slot')) > mediaContent (slot inside mediaContent) > legacy component
 */
const getCustomProp = (entry, key) => {
  if (entry?.customProps) {
    try {
      const props = JSON.parse(entry.customProps);
      if (props && typeof props === 'object' && !Array.isArray(props)) {
        return props[key];
      }
      return undefined;
    } catch {
      return undefined;
    }
  }
  return undefined;
};

export const normalizeSpotlightContainerData = ({
  slotResp,
  componentData,
  maxSlots,
  overflowBehavior = 'Wrap',
  extraCardOnLargeAndAbove = false,
  mediaCollectionIndex,
}) => {

  if (slotResp?.slot) {
    const slot = slotResp?.slot;
    const mediaCollections = slot.mediaCollections || [];
    const fallbackMediaCollection = mediaCollections[0] || null;
    const mediaCollection = mediaCollections[mediaCollectionIndex] || fallbackMediaCollection;
    const mediaArray = mediaCollection.media;
    const limitedMedia = typeof maxSlots === 'number' ? mediaArray.slice(0, maxSlots) : mediaArray;

    const spotlightList = limitedMedia.map((media, index) => {
      const desktopSource = media?.sourceDesktop?.[0] || null;
      const mobileSource = media?.sourceMobile?.[0] || null;
      const effectiveSource = desktopSource || mobileSource || {};
      const typeString = String(effectiveSource?.type || '').toLowerCase();
      const isVideo = typeString.includes('video');
      const linkList = getCustomProp(effectiveSource, 'linkList');
      const proAnalyticsCampaign = getCustomProp(media, 'proAnalyticsCampaign');
      const proAnalyticsComponent = getCustomProp(media, 'proAnalyticsComponent');

      const spotlight = {
        __typename: 'Spotlight',
        id: `${media.entryId}-${index}`,
        componentName: 'Spotlight',
        title: media.title,
        description: media.description || '',
        link: media.link || effectiveSource?.hyperlink || '',
        cta: media.callToAction || 'Shop Now',
        videoUrl: isVideo ? effectiveSource?.uri : null,
        richText: media.richText || null,
        itemTheme: media.theme || null,
        linkList,
        proAnalyticsCampaign,
        proAnalyticsComponent
      };

      if (!isVideo && effectiveSource?.uri) {
        spotlight.previewImage = { damDownloadedContent: { url: effectiveSource.uri } };
      }

      return spotlight;
    });

    const mediaCollectionOverflowBehavior = getCustomProp(mediaCollection, 'overflowBehavior');
    const mediaCollectionExtraCardOnLargeAndAbove = getCustomProp(mediaCollection, 'extraCardOnLargeAndAbove');
    const mediaCollectionItemTheme = getCustomProp(mediaCollection, 'itemTheme');

    return {
      id: slot.id,
      entryId: slot.entryId,
      containerTitle: mediaCollection.title || null,
      spotlightList,
      overflowBehavior: mediaCollectionOverflowBehavior || overflowBehavior,
      extraCardOnLargeAndAbove: mediaCollectionExtraCardOnLargeAndAbove || extraCardOnLargeAndAbove,
      theme: mediaCollection.theme || null,
      itemTheme: mediaCollectionItemTheme || null,
    };
  }

  // Fallback to legacy component data shape if present
  if (componentData?.component) {
    const containerNode = componentData.component.SpotlightContainer || componentData.component;
    return {
      id: containerNode.id,
      title: containerNode.title,
      spotlightList: containerNode.spotlightList || [],
      overflowBehavior: containerNode.overflowBehavior || 'None',
      extraCardOnLargeAndAbove: containerNode.extraCardOnLargeAndAbove || false,
      theme: containerNode.theme,
      itemTheme: containerNode.itemTheme,
    };
  }

  return null;
};
